import { ref, inject } from '@nuxtjs/composition-api';
import { eventDateFormat } from '~/modules/common/utils/dateUtils';
import { useInfiniteScroll } from '~/composables/useInfiniteScroll';
import { LANG } from '~/constants/config';
import { getArticleCategoryPathNames } from '~/modules/article/utils/articleUtils';
import i18n from '~/i18n';
const noContentMsg = i18n.messages[LANG]['news-page']['no-content'];

export function generateGA4Article({ article, adConfig, position, list, quantity, type, listId }) {
  const entryNum = article?.entries?.length;
  let articleLength;
  if (entryNum) {
    if (entryNum <= 5) {
      articleLength = 'Kısa';
    } else if (entryNum <= 9) {
      articleLength = 'Orta';
    } else if (entryNum <= 14) {
      articleLength = 'Uzun';
    } else {
      articleLength = 'Çok Uzun';
    }
  }
  return {
    item_name: article.title || noContentMsg, // İçeriğin adı yazılmalıdır.
    item_id: article.legacyId, // İçeriğin ID'si yazılmalıdır.
    price: 1.0, // Sabit olarak bu yazılacak.
    item_brand: article.author?.username || article.authors?.[0]?.username || '~no-username', // İçeriğin editörü yazılacak.
    item_category: article.breadcrumb?.[0]?.name || article.categories?.[0]?.name, // Ürünün 1. kategori bilgisi yazılacak.
    ...(article.breadcrumb?.[1] && { item_category2: article.breadcrumb[1]?.name }),
    ...(entryNum && { item_category3: articleLength }),
    ...(adConfig && { item_category4: adConfig?.value?.decidedMode?.value === 'sponsored' }),
    ...(article.breadcrumb?.[2] && { item_category5: article.breadcrumb[2]?.name }),
    item_variant: article.dates ? eventDateFormat(article.dates.created) : '', // Ürünün yayın tarihi yazılacak.
    ...(list && { item_list_name: list }),
    ...(type && { ...type }),
    ...(position && { index: position }),
    ...(quantity && { quantity }),
    ...(listId && { item_list_id: listId }),
  };
}

export const sendDataLayer = ({ list = '', item = {}, position = 0, type = '', listId } = {}, events = []) => {
  const isDataPushed = ref({});
  const isDataLayerSent = () => isDataPushed.value?.scroll || isDataPushed.value?.initOnLoad || isDataPushed.value?.init;
  let elRef, stopEvent, clickEvent, initOnLoadEvent, initEvent;
  if (events.includes('scroll') && !isDataPushed.value?.scroll) {
    const customDataLayer = inject('dataLayer');
    [elRef, stopEvent] = useInfiniteScroll(
      () => {
        const { ua, ga } = mapArticleData('content_impression', list, item, position, type, null, listId);
        customDataLayer.value.type = type;
        customDataLayer.value.ua.push(ua);
        customDataLayer.value.ga.push(ga);
        isDataPushed.value.scroll = true;
        stopEvent();
      },
      {
        threshold: 0.5,
      }
    );
  }
  if (events.includes('click')) {
    clickEvent = (_, eventLabel) => {
      const data = mapArticleData('content_click', list, item, position, type, eventLabel, listId);
      sendUAGALayers(data);
    };
  }
  if (events.includes('initOnLoad') && !isDataPushed.value?.initOnLoad) {
    initOnLoadEvent = () => {
      const customDataLayer = inject('dataLayer');
      const { ga } = mapArticleData('content_impression', list, item, position);
      customDataLayer.value.ga.push(ga);
      isDataPushed.value.initOnLoad = true;
    };
  }
  if (events.includes('init') && !isDataPushed.value?.init) {
    initEvent = ({ preventDuplicate = false } = {}) => {
      if (preventDuplicate && isDataLayerSent()) return;
      const { ga } = mapArticleData('content_impression', list, item, position);
      window.dataLayer.push({
        ecommerce: null,
      });
      window.dataLayer.push({
        event: 'view_item_list', // Sabit olarak bu değer yazılmalıdır.
        ecommerce: {
          items: [ga],
        },
      });
      isDataPushed.value.init = true;
    };
  }
  return { elRef, clickEvent, initOnLoadEvent, initEvent };
};
export const mapArticleData = (event, list, item, position, type, eventLabel, listId) => {
  if (!event || !list || !item) {
    return false;
  }
  const category = item.breadcrumb?.map((category) => category.name).join('/') || item.categories?.[0]?.name;
  const categoryPathNames = getArticleCategoryPathNames(item);
  if (event === 'content_impression') {
    return {
      ga: generateGA4Article({ article: item, list, position, type, listId }),
    };
  } else if (event === 'content_click') {
    return {
      ua: {
        event, // Sabit olarak bu değer yazılmalıdır.
        ecommerce: {
          click: {
            actionField: {
              list, //
            },
            products: [
              {
                name: item.title || noContentMsg, // İçerik başlığı yazılmalıdır.
                id: item.legacyId, // İçerik ID'si yazılmalıdır.
                price: '1.00', // Sabit olarak bu tutar yazılmalıdır.
                brand: item.author?.username || '~no-username', // Editör adı yazılmalıdır.
                category, // İçeriğin kategori hiyerarşisi yazılmalıdır.
                variant: item.dates ? eventDateFormat(item.dates.created) : '', // İçeriğin oluşturulma tarihi yazılmalıdır.
                position,
                ...(categoryPathNames?.[1] && { dimension2: categoryPathNames[1] }),
                ...(categoryPathNames?.[2] && { dimension6: categoryPathNames[2] }),
              },
            ],
          },
        },
      },
      ga: {
        event: 'select_item', // Sabit olarak bu değer yazılmalıdır.
        ...(eventLabel && { event_label: eventLabel }),
        ecommerce: {
          ...(type && { ...type }),
          items: [generateGA4Article({ article: item, list, position, type, listId })],
        },
      },
    };
  } else {
    return false;
  }
};

export const sendReactionCommentLayer = (type, { article, adConfig }, data, list, listId, social) => {
  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
  }
  const category = article.breadcrumb?.map((category) => category.name).join('/');
  const gaCookie = getCookie('_ga') || '';
  const timestamp = +new Date();
  let reactionType;

  if (data) {
    const urlSplit = data.icons.svg.split('/');
    reactionType = urlSplit[urlSplit.length - 1].split('.')[0];
  }

  const _type = type === 'reaction' ? 'Reaksiyon' : type === 'comment' ? 'Yorum' : type;
  const _subtype = data ? reactionType : type === 'comment' ? 'Yorum' : social || type;

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'interaction_done',
    ecommerce: {
      purchase: {
        actionField: {
          id: `${timestamp}.${gaCookie}`,
          affiliation: 'Web',
          revenue: '1.00',
          tax: '0.00',
          shipping: '0.00',
          type: type === 'reaction' ? 'Reaksiyon' : 'Yorum', // Bu alan "Yorum" ya da "Reaksiyon" olarak gelmelidir.
          subType: data ? reactionType : 'Yorum', // Eğer reaksiyon yapıldıysa bu alana reaksiyonun tipi, yorum yapıldıysa yine "Yorum" yazılmalıdır.
        },
        products: [
          {
            name: article.title || noContentMsg,
            id: article.legacyId,
            price: '1.00',
            brand: article.authors?.[0]?.username || '~no-username',
            category,
            variant: eventDateFormat(article.dates.created),
            quantity: 1,
          },
        ],
      },
    },
  });
  // GA4
  window.dataLayer.push({
    ecommerce: null,
  });
  window.dataLayer.push({
    event: 'purchase', // Sabit olarak bu değer yazılmalıdır.
    ecommerce: {
      transaction_id: `${timestamp}.${gaCookie}`, // UA için gönderilen transaction burada da gönderilmelidir.
      affiliation: 'Web', // Burası sabit olarak Web gelmelidir.
      value: 1,
      tax: 0,
      shipping: 0,
      currency: 'TRY',
      type: _type,
      subType: _subtype,
      items: [generateGA4Article({ article, quantity: 1, adConfig, list, listId })],
    },
  });
};

export const sendContentReadEvent = (article, adConfig) => {
  const contentReadDataLayer = () => {
    const category = article.breadcrumb?.map((category) => category.name).join('/');
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'content_read',
      ecommerce: {
        currencyCode: 'TRY',
        add: {
          products: [
            {
              name: article.title || noContentMsg,
              id: article.legacyId,
              price: '1.00',
              brand: article.authors?.[0]?.username || '~no-username',
              category,
              variant: eventDateFormat(article.dates.created),
              quantity: 1,
            },
          ],
        },
      },
    });
    // GA4
    window.dataLayer.push({
      ecommerce: null,
    });
    window.dataLayer.push({
      event: 'add_to_cart', // Sabit olarak bu değer yazılmalıdır.
      ecommerce: {
        items: [generateGA4Article({ article, quantity: 1, adConfig })],
      },
    });
    contentReadStop();
  };

  const [articleContentReadRef, contentReadStop] = useInfiniteScroll(contentReadDataLayer);
  return articleContentReadRef;
};

export const getPageType = () =>
  window.dataLayer.find(function (obj) {
    return obj.event === 'PageView';
  })?.page?.type || '';

export const clearMenuImpressionTimer = () => {
  clearTimeout(window.menuImpressionTimeout);
};

export const sendWidgetEvent = (type, widget, name, position) => {
  if (type === 'view') {
    // window.dataLayer.push({
    //   event: 'promotion_view',
    //   ecommerce: {
    //     promoView: {
    //       promotions: [
    //         {
    //           id: 'Anasayfa', // Sabit olarak bu isim yazılmalıdır.
    //           name: `Anasayfa - ${widget}`, // {{Page Type}} - {{İçerik Başlığı}}
    //           creative: name, // İlgili elementin başlığı gelmelidir. Yazio'da yazar adı, astrolojide burç adı, hashtag'de etiket adı
    //           position, //
    //         },
    //         {
    //           id: 'Anasayfa', // Sabit olarak bu isim yazılmalıdır.
    //           name: `Anasayfa - ${widget}`, // {{Page Type}} - {{İçerik Başlığı}}
    //           creative: name, // İlgili elementin başlığı gelmelidir. Yazio'da yazar adı, astrolojide burç adı, hashtag'de etiket adı
    //           position, //
    //         },
    //       ],
    //     },
    //   },
    // });
    window.dataLayer.push({
      event: 'view_promotion', // Sabit olarak bu değer yazılmalıdır.
      ecommerce: {
        items: [
          {
            promotion_id: 'Anasayfa', // Sabit olarak bu isim yazılmalıdır.
            promotion_name: `Anasayfa - ${widget}`, // {{Page Type}} - {{İçerik Başlığı}}
            creative_name: name, // İlgili elementin başlığı gelmelidir. Yazio'da yazar adı, astrolojide burç adı, hashtag'de etiket adı
            creative_slot: position, //
          },
          {
            promotion_id: 'Anasayfa', // Sabit olarak bu isim yazılmalıdır.
            promotion_name: `Anasayfa - ${widget}`, // {{Page Type}} - {{İçerik Başlığı}}
            creative_name: name, // İlgili elementin başlığı gelmelidir. Yazio'da yazar adı, astrolojide burç adı, hashtag'de etiket adı
            creative_slot: position, //
          },
        ],
      },
    });
  } else if (type === 'click') {
    window.dataLayer.push({
      event: 'promotion_click',
      ecommerce: {
        promoClick: {
          promotions: [
            {
              id: 'Anasayfa', // Sabit olarak bu isim yazılmalıdır.
              name: `Anasayfa - ${widget}`, // {{Page Type}} - {{İçerik Başlığı}}
              creative: name, // İlgili elementin başlığı gelmelidir. Yazio'da yazar adı, astrolojide burç adı, hashtag'de etiket adı
              position, //
            },
          ],
        },
      },
    });
    window.dataLayer.push({
      event: 'select_promotion', // Sabit olarak bu değer yazılmalıdır.
      ecommerce: {
        items: [
          {
            promotion_id: 'Anasayfa', // Sabit olarak bu isim yazılmalıdır.
            promotion_name: `Anasayfa - ${widget}`, // {{Page Type}} - {{İçerik Başlığı}}
            creative_name: name, // İlgili elementin başlığı gelmelidir. Yazio'da yazar adı, astrolojide burç adı, hashtag'de etiket adı
            creative_slot: position, //
          },
        ],
      },
    });
  }
};

export const sendVideoWidgetEvent = (type, list, position) => {
  const items = list.map((article) => {
    return {
      item_name: article.title || noContentMsg,
      item_id: article.legacyId,
      price: 1,
      item_brand: article.author?.username || article.authors?.[0]?.username || '~no-username',
      item_category: article.breadcrumb?.[0].name || article.categories?.[0]?.name,
      item_category2: article.breadcrumb?.[1] ? article.breadcrumb[1].name : '',
      item_category3: '',
      item_category4: '',
      ...(article.position && { position: article.position }),
    };
  });
  if (type === 'view') {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'view_promotion', // Sabit olarak bu değer yazılmalıdır.
      ecommerce: {
        creative_name: '1184693',
        creative_slot: position,
        promotion_id: 'Video Widget',
        promotion_name: 'Anasayfa - Video Widget',
        items,
      },
    });
  } else if (type === 'click') {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'select_promotion', // Sabit olarak bu değer yazılmalıdır.
      ecommerce: {
        creative_name: '1184693',
        creative_slot: position,
        promotion_id: 'Video Widget',
        promotion_name: 'Anasayfa - Video Widget',
        items,
      },
    });
  } else if (type === 'view_item_list') {
    const { ga } = mapArticleData('content_impression', 'Video Widget', list[0], position);
    window.dataLayer.push({
      ecommerce: null,
    });
    window.dataLayer.push({
      event: 'view_item_list', // Sabit olarak bu değer yazılmalıdır.
      ecommerce: {
        items: [ga],
      },
    });
  } else if (type === 'select_item') {
    const { ua, ga } = mapArticleData('content_click', 'Video Widget', list[0], position);
    window.dataLayer.push(ua);
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(ga);
  }
};

export const sendMenuImpressionEvent = (device, { currentCategory, subCategories }) => {
  const pageType = getPageType();
  if (device === 'desktop') {
    clearMenuImpressionTimer();
    window.menuImpressionTimeout = setTimeout(() => {
      window.dataLayer.push({
        event: 'promotion_view',
        ecommerce: {
          promoView: {
            promotions: [
              {
                id: 'Main Header', // Sabit olarak bu isim yazılmalıdır.
                name: `${pageType} - Main Header - ${currentCategory.shortName}`, // {{Page Type}} - Main Header - {{Menu Item}}
                creative: 'Other', // Gösterim anında sabit gelmeli.
                position: 'Other', //
              },
            ],
          },
        },
      });
      window.dataLayer.push({
        event: 'view_promotion', // Sabit olarak bu değer yazılmalıdır.
        ecommerce: {
          items: [
            {
              promotion_id: 'Main Header', // Sabit olarak bu isim yazılmalıdır.
              promotion_name: `${pageType} - Main Header - ${currentCategory.shortName}`, // {{Page Type}} - Main Header - {{Menu Item}}
              creative_name: 'Other', // Gösterim anında sabit gelmeli.
              creative_slot: 'Other',
            },
          ],
        },
      });
    }, 1000);
  } else {
    clearMenuImpressionTimer();
    window.menuImpressionTimeout = setTimeout(() => {
      window.dataLayer.push({
        event: 'promotion_view',
        ecommerce: {
          promoView: {
            promotions: subCategories.map((category, index) => ({
              id: 'Mobile Header', // Sabit olarak bu isim yazılmalıdır.
              name: `${pageType} - Mobile Header - ${currentCategory.name}`, // {{Page Type}} - Main Header - {{Menu Item}}
              creative: category.name, //
              position: index + 1, //
            })),
          },
        },
      });
      window.dataLayer.push({
        event: 'view_promotion', // Sabit olarak bu değer yazılmalıdır.
        ecommerce: {
          items: subCategories.map((category, index) => ({
            promotion_id: 'Mobile Header', // Sabit olarak bu isim yazılmalıdır.
            promotion_name: `${pageType} - Mobile Header - ${currentCategory.name}`, // {{Page Type}} - Mobile Header - {{Menu Item}}
            creative_name: category.name,
            creative_slot: index + 1,
          })),
        },
      });
    }, 1000);
  }
};

export const sendMenuClickEvent = (device, { firstLevel, secondLevel, currentCategory, position }) => {
  const pageType = getPageType();
  if (device === 'desktop') {
    window.dataLayer.push({
      event: 'promotion_click',
      ecommerce: {
        promoClick: {
          promotions: [
            {
              id: 'Main Header', // Sabit olarak bu isim yazılmalıdır.
              name: `${pageType} - Main Header - ${currentCategory.shortName}`, // {{Page Type}} - Main Header - {{Menu Item}}
              creative: firstLevel, // Tıklanan içeriğin ana başlığı yazılmalıdır. Galatasaray'a tıklasa da Futbol yazılmalı.
              position: secondLevel, // Tıklanan Click Text yazılmalı.
            },
          ],
        },
      },
    });
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'select_promotion', // Sabit olarak bu değer yazılmalıdır.
      ecommerce: {
        items: [
          {
            promotion_id: 'Main Header', // Sabit olarak bu isim yazılmalıdır.
            promotion_name: `${pageType} - Main Header - ${currentCategory.shortName}`, // {{Page Type}} - Main Header - {{Menu Item}}
            creative_name: firstLevel, // Tıklanan içeriğin ana başlığı yazılmalıdır. Galatasaray'a tıklasa da Futbol yazılmalı.
            creative_slot: secondLevel, // Tıklanan Click Text yazılmalı.
          },
        ],
      },
    });
  } else {
    window.dataLayer.push({
      event: 'promotion_click',
      ecommerce: {
        promoClick: {
          promotions: [
            {
              id: 'Mobile Header', // Sabit olarak bu isim yazılmalıdır.
              name: `${pageType} - Main Header - ${currentCategory.name}`, // {{Page Type}} - Main Header - {{Menu Item}}
              creative: firstLevel, //
              position, //
            },
          ],
        },
      },
    });
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'select_promotion', // Sabit olarak bu değer yazılmalıdır.
      ecommerce: {
        items: [
          {
            promotion_id: 'Mobile Header', // Sabit olarak bu isim yazılmalıdır.
            promotion_name: `${pageType} - Main Header - ${currentCategory.name}`, // {{Page Type}} - Mobile Header - {{Menu Item}}
            creative: firstLevel, //
            position, //
          },
        ],
      },
    });
  }
};

export const sendUAGALayers = ({ ua, ga }) => {
  window.dataLayer.push(ua);
  window.dataLayer.push({
    ecommerce: null,
  });
  window.dataLayer.push(ga);
};
